/**
 * S1 Door — the session probe and the state machine behind it (seam S1).
 *
 * The door has exactly five states (UX v0.3 §5, S1). Which one renders is a
 * pure function of the probe against `GET /v1/session`, so the decision is
 * testable without a DOM and cannot drift from the API's refusal codes.
 *
 * Identity is Cloudflare Access's alone (I-2). Nothing here stores a token,
 * mints a session or remembers a person between loads.
 */

import { API_BASE } from "./api";

/** The five S1 states. `signed-in` is the edge state — the door is skipped. */
export type DoorState = "loading" | "ideal" | "error-account" | "error-provider" | "signed-in";

/** Who the control API says is signed in. Carries no entitlement (I-3). */
export interface SessionPerson {
  email: string;
  display_name: string;
}

export interface SessionResponse {
  person: SessionPerson;
  identity_source: string;
}

/** A refusal from `GET /v1/session`, carrying the API's stable reason code. */
export interface SessionRefusal {
  status: number;
  /** One of the `IdentityError` reason codes, or `unreachable` if the call failed. */
  reason: string;
  /** The API's microcopy — the same sentence the door renders. */
  message: string;
}

export type SessionProbe =
  | { kind: "session"; session: SessionResponse }
  | { kind: "refused"; refusal: SessionRefusal };

/**
 * Map a probe result to the door state.
 *
 * The mapping is deliberately explicit rather than "anything not 200 is an
 * error": `no_identity` is the *normal* first visit and must render the ideal
 * door, not a failure. Treating a missing sign-in as an error is the bug this
 * function exists to make impossible.
 */
export function resolveDoorState(probe: SessionProbe): DoorState {
  if (probe.kind === "session") return "signed-in";

  switch (probe.refusal.reason) {
    // Not signed in yet, or the session lapsed: this is what the door is for.
    case "no_identity":
    case "expired_identity":
    case "invalid_identity":
      return "ideal";

    // Verified, but not a Ketosource account.
    case "wrong_account":
      return "error-account";

    // The identity edge itself cannot answer — unconfigured, JWKS unreachable,
    // or the control API is down. Signing in again would not help.
    case "not_configured":
    case "identity_unavailable":
    case "unreachable":
      return "error-provider";

    default:
      // An unrecognised refusal is a provider fault, not a silent ideal door:
      // showing a working sign-in button for a state we do not understand sends
      // the person into a loop with no explanation.
      return "error-provider";
  }
}

/**
 * Probe the control API for the current Access session.
 *
 * Never throws: a network failure resolves to a `refused` probe with reason
 * `unreachable`, so the caller has one shape to reason about and the door can
 * never render a blank screen on an exception.
 */
export async function probeSession(signal?: AbortSignal): Promise<SessionProbe> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/v1/session`, { credentials: "include", signal });
  } catch {
    return {
      kind: "refused",
      refusal: {
        status: 0,
        reason: "unreachable",
        message: "Sign-in is unavailable right now.",
      },
    };
  }

  if (res.ok) {
    return { kind: "session", session: (await res.json()) as SessionResponse };
  }

  let reason = "unreachable";
  let message = "Sign-in is unavailable right now.";
  try {
    const body = (await res.json()) as { error?: string; message?: string };
    if (body.error) reason = body.error;
    if (body.message) message = body.message;
  } catch {
    // A refusal with no JSON body (an edge error page, say) keeps the defaults.
  }
  return { kind: "refused", refusal: { status: res.status, reason, message } };
}

/**
 * Where the sign-in button sends the browser.
 *
 * Signing in is a *navigation to a protected path*, not an API call: the
 * Cloudflare Access application intercepts it, runs the Google Workspace OIDC
 * redirect, and returns the browser here with the `CF_Authorization` cookie set.
 * The door path itself is bypassed at the edge so it can render to a signed-out
 * visitor — see `infra/README.md` for the Access policy that makes this true.
 */
export const SHELL_PATH = "/app";
